import { ECreditCardBrands, EFundsOperation } from '@enum';
import { TMyLoyalty } from '@xeppt/xeppt-sdk/types/loyalty';
import { ETransactionAction, ETransactionMethod, ETransactionType } from '@xeppt/xeppt-sdk/types';
import { paymentsConfig } from '@const/payment';

export const getCardBrand = (card_number: string) => {
    const cleanedCardNumber = card_number.replace(/\D/g, ''); // Remove all non-digit characters

    if (/^4[0-9]{12}(?:[0-9]{3})?$/.test(cleanedCardNumber)) {
        return ECreditCardBrands.VISA;
    } else if (/^5[1-5][0-9]{14}$/.test(cleanedCardNumber)) {
        return ECreditCardBrands.MASTERCARD;
    } else if (/^3[47][0-9]{13}$/.test(cleanedCardNumber)) {
        return ECreditCardBrands.AMEX;
    } else if (/^6(?:011|5[0-9]{2})[0-9]{12}$/.test(cleanedCardNumber)) {
        return ECreditCardBrands.DISCOVER;
    } else {
        return;
    }
};

export const getFeesPlanSendAndRequest = ({
    loyalty,
    method
}: {
    loyalty?: TMyLoyalty;
    method: EFundsOperation;
}) => {
    const plan = loyalty?.current_plan;
    //@ts-ignore
    if (plan?.[method]) return plan?.[method];
    return undefined;
};

export const getInternalTransferAction = (
    method: ETransactionMethod,
    action: ETransactionAction,
    type: ETransactionType
) => {
    return Object.keys(paymentsConfig).find((key) => {
        //@ts-ignore
        const config = paymentsConfig[key as EFundsOperation];
        return config.action === action && config.method === method && config.type === type;
    }) as EFundsOperation | undefined;
};

// A XEPPT fee is a single COMBINED rate = percent (%) + flat ($), both configured
// per operation/tier in admin → Fees. Render each component ONLY when it is greater
// than zero, so a percent-only fee shows "3%", a flat-only fee shows "1 CAD", and a
// combined fee shows "3% + 1 CAD" — never a phantom "0%" or "0 CAD" (founder rule).
// Returns '' when both components are zero/absent so callers can fall back.
// Currency is parameterised (never hardcode CAD); `renderFlat` lets a surface keep
// its existing money style (e.g. "$1.00" vs "1.00 CAD").
export const formatFeeRate = ({
    percent,
    flat,
    currency = 'CAD',
    renderFlat
}: {
    percent?: number | null;
    flat?: number | null;
    currency?: string;
    renderFlat?: (flat: number, currency: string) => string;
}): string => {
    // A negative percent/flat is never a valid fee — it can only come from a
    // misconfigured admin → Fees value. The `> 0` guards below silently treat it
    // as "no component" (renders "Free"), which would hide the misconfiguration.
    // Surface it loudly in non-production so it is caught before reaching users;
    // production stays silent (no console noise) and still fails safe to "Free".
    if (process.env.NODE_ENV !== 'production') {
        if (typeof percent === 'number' && percent < 0) {
            console.error(`[formatFeeRate] negative percent fee (${percent}) — check admin → Fees`);
        }
        if (typeof flat === 'number' && flat < 0) {
            console.error(`[formatFeeRate] negative flat fee (${flat}) — check admin → Fees`);
        }
    }
    const parts: string[] = [];
    if (typeof percent === 'number' && percent > 0) {
        parts.push(`${+percent.toFixed(2)}%`);
    }
    if (typeof flat === 'number' && flat > 0) {
        parts.push(renderFlat ? renderFlat(flat, currency) : `${+flat.toFixed(2)} ${currency}`);
    }
    return parts.join(' + ');
};
